import axios from './companyHttp'

export const ONBOARDING_API = '/api/v3/driver-onboarding/'
export type Resolution = 'PENDING' | 'COMPLETED' | 'BYPASSED' | 'NEEDS_CORRECTION'
export interface OnboardingDocument {
  expires_on?: string | null
  expired?: boolean
  id: string
  name: string
  uploaded_at: string
  uploaded_by: string
  current: boolean
  replaces: string | null
}
export interface OnboardingStep {
  assigned_at?: string | null
  assignment_completed_at?: string | null
  draft?: Record<string, any> | Employment[] | null
  draft_saved_at?: string | null
  submitted_at?: string | null
  expires_on?: string | null
  expired?: boolean
  kind: string
  label: string
  required: boolean
  resolution: Resolution
  reason: string
  resolved_at: string | null
  resolved_by: string | null
  order_status: 'NONE' | 'QUEUED' | 'SUBMITTING' | 'SUBMITTED' | 'UNKNOWN' | 'FAILED'
  requested_at: string | null
  requested_by: string | null
  provider_account: string
  last_error: string
  unavailable_reason: string
  has_document: boolean
  has_report: boolean
  documents: OnboardingDocument[]
  result: {
    status: string
    ready_for_review: boolean
    terminal?: boolean
    outcome?: string
    appointment_url?: string | null
    employment_reports?: { id: string; employer: string }[]
    summary: Record<string, string>[]
    medical_certificates?: { status: string; expiration_date: string }[]
  }
}
export interface DriverDetails {
  first_name: string
  middle_name: string
  last_name: string
  email: string
  mobile: string
  dob: string | null
  address: { line1: string; line2: string; city: string; state: string; zip: string }
}
export interface CDL {
  license_number: string
  license_state: string
  license_issued: string | null
  license_expiration: string | null
  has_ssn: boolean
}
export interface Employment {
  job_type: 'driving' | 'not_driving'
  start_date: string
  end_date: string | null
  company_name: string
  company_address: string
  position_held: string
  can_contact_employer: boolean
  contact_name: string
  contact_email: string
  contact_phone: string
  subject_to_fmcsr_regulations: boolean
  subject_to_drug_and_alcohol_testing: boolean
  leaving_reason_detail: string
}
export interface OnboardingEquipment {
  vehicle_number: string | null
  vin_number: string | null
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_year: number | null
  ownership_type: string | null
}
export interface DriverOnboarding {
  equipment?: OnboardingEquipment | null
  assignment_email_status?: string
  expiration_warnings?: string[]
  intake_locked?: boolean
  id: string
  date_updated: string
  mode: 'EXISTING' | 'GUIDED'
  status: 'IN_PROGRESS' | 'ELIGIBLE'
  eligible_at: string | null
  eligible_by: string | null
  eligibility_review_notes: string
  user_id: string
  team: { id: string; name: string }
  details: DriverDetails
  cdl: CDL
  employment: Employment[]
  truck: string | null
  work_location_state: string
  steps: OnboardingStep[]
  intake_errors: string[]
  eligibility_errors: string[]
}
export interface DriverSummary {
  expiration_warnings?: string[]
  id: string
  name: string
  email: string
  team_name: string
  mode: string
  status: string
}
export interface OnboardingChoices {
  company?: { id: string } | null
  teams: { id: string; name: string }[]
  trucks: { id: string; team_id: string; vehicle_number: string; vin_number: string }[]
}
export interface OnboardingEvent {
  at: string
  step: string
  resolution: Resolution
  reason: string
  actor: string | null
  requested_by: string | null
  order_status: string
}
export function onboardingError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 409)
      return 'This onboarding changed. Reload the latest version before saving.'
    if ([403, 404].includes(error.response?.status || 0))
      return 'This onboarding is unavailable to your account.'
    if (error.response?.status === 400) {
      const flatten = (value: unknown): string[] => {
        if (typeof value === 'string') return [value]
        if (Array.isArray(value)) return value.flatMap(flatten)
        if (value && typeof value === 'object')
          return Object.entries(value).flatMap(([key, val]) =>
            flatten(val).map((text) => (key === 'detail' ? text : `${key.replaceAll('_', ' ')}: ${text}`))
          )
        return []
      }
      return flatten(error.response.data).join(' ')
    }
  }
  return 'Unable to complete this action. Please try again.'
}
export async function getDriverOnboarding(id: string): Promise<DriverOnboarding> {
  return (await axios.get(`${ONBOARDING_API}${id}/`)).data
}
export async function listDriverOnboarding(
  page: number,
  search: string
): Promise<{ count: number; results: DriverSummary[] }> {
  return (await axios.get(ONBOARDING_API, { params: { page, search } })).data
}
export async function getOnboardingChoices(): Promise<OnboardingChoices> {
  return (await axios.get(`${ONBOARDING_API}choices/`)).data
}
export async function createDriverOnboarding(input: unknown): Promise<DriverOnboarding> {
  return (await axios.post(ONBOARDING_API, input)).data
}
export async function saveDriverOnboarding(
  record: DriverOnboarding,
  input: unknown
): Promise<DriverOnboarding> {
  return (
    await axios.patch(`${ONBOARDING_API}${record.id}/`, {
      ...(input as object),
      expected_date_updated: record.date_updated,
    })
  ).data
}
export async function actOnOnboarding(
  record: DriverOnboarding,
  kind: string,
  action: string,
  reason: string,
  expiresOn?: string
): Promise<DriverOnboarding> {
  return (
    await axios.post(`${ONBOARDING_API}${record.id}/step/`, {
      kind,
      action,
      reason,
      ...(expiresOn !== undefined ? { expires_on: expiresOn || null } : {}),
      expected_date_updated: record.date_updated,
    })
  ).data
}
export async function markDriverEligible(
  record: DriverOnboarding,
  reviewNotes: string
): Promise<DriverOnboarding> {
  return (
    await axios.post(`${ONBOARDING_API}${record.id}/eligible/`, {
      expected_date_updated: record.date_updated,
      review_confirmed: true,
      review_notes: reviewNotes,
    })
  ).data
}
export async function uploadOnboardingDocument(
  record: DriverOnboarding,
  kind: string,
  file: File,
  replaces?: string,
  expiresOn?: string
): Promise<DriverOnboarding> {
  const data = new FormData()
  data.append('file', file)
  data.append('kind', kind)
  data.append('expected_date_updated', record.date_updated)
  if (replaces) data.append('replaces', replaces)
  if (expiresOn) data.append('expires_on', expiresOn)
  return (await axios.post(`${ONBOARDING_API}${record.id}/documents/`, data)).data
}
export async function uploadEmploymentRelease(
  record: DriverOnboarding,
  file: File
): Promise<DriverOnboarding> {
  const data = new FormData()
  data.append('file', file)
  data.append('expected_date_updated', record.date_updated)
  return (await axios.post(`${ONBOARDING_API}${record.id}/release/`, data)).data
}
export async function getOnboardingHistory(id: string): Promise<OnboardingEvent[]> {
  return (await axios.get(`${ONBOARDING_API}${id}/history/`)).data
}

export const ASSIGNED_ONBOARDING_PATH = '/driver-onboarding/'
export const assignableSections = ['DETAILS', 'EMPLOYMENT_HISTORY', 'CDL', 'EQUIPMENT', 'CONSENT']
export type ApplicationRecord = Pick<
  DriverOnboarding,
  'id' | 'date_updated' | 'status' | 'team' | 'intake_locked'
> &
  Partial<Pick<DriverOnboarding, 'details' | 'cdl' | 'employment' | 'work_location_state' | 'equipment'>> & {
    steps: Pick<
      OnboardingStep,
      | 'kind'
      | 'label'
      | 'resolution'
      | 'assigned_at'
      | 'assignment_completed_at'
      | 'draft'
      | 'draft_saved_at'
      | 'submitted_at'
    >[]
  }
export async function assignOnboarding(
  record: DriverOnboarding,
  kinds: string[],
  resend = false
): Promise<DriverOnboarding> {
  return (
    await axios.post(`${ONBOARDING_API}${record.id}/assignments/`, {
      expected_date_updated: record.date_updated,
      kinds,
      resend,
    })
  ).data
}
export async function saveOnboardingDraft(
  record: DriverOnboarding,
  kind: string,
  data: unknown
): Promise<DriverOnboarding> {
  return (
    await axios.post(`${ONBOARDING_API}${record.id}/draft/`, {
      expected_date_updated: record.date_updated,
      kind,
      data,
    })
  ).data
}
export async function reviewOnboardingDraft(
  record: DriverOnboarding,
  kind: string,
  discard = false,
  truck?: string
): Promise<DriverOnboarding> {
  return (
    await axios.post(`${ONBOARDING_API}${record.id}/review-draft/`, {
      expected_date_updated: record.date_updated,
      kind,
      discard,
      ...(truck ? { truck } : {}),
    })
  ).data
}
export async function getAssignedOnboarding(): Promise<ApplicationRecord[]> {
  return (await axios.get(`${ONBOARDING_API}assigned/`)).data
}
export async function getAssignedApplication(id: string): Promise<ApplicationRecord> {
  return (await axios.get(`${ONBOARDING_API}${id}/application/`)).data
}
export async function saveAssignedSection(
  record: ApplicationRecord,
  kind: string,
  data: unknown,
  submit = false
): Promise<ApplicationRecord> {
  return (
    await axios.patch(`${ONBOARDING_API}${record.id}/application/`, {
      expected_date_updated: record.date_updated,
      kind,
      data,
      submit,
    })
  ).data
}
export async function saveOnboardingExpiration(
  record: DriverOnboarding,
  kind: string,
  expiresOn: string
): Promise<DriverOnboarding> {
  return (
    await axios.post(`${ONBOARDING_API}${record.id}/step/`, {
      expected_date_updated: record.date_updated,
      kind,
      action: 'expiration',
      expires_on: expiresOn || null,
    })
  ).data
}
